import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../../../helpers/apiClient.helper';
import { worldCupQueryKeys } from '../constants/worldcupQueryKeys';
import {
  GC_TIME_WORLD_CUP_MS,
  STALE_TIME_WORLD_CUP_LEADERBOARD_MS,
} from '../constants/worldcupReactQuery.constants';
import { fetchAllGroupLeaderboardPages } from '../helpers/fetchAllGroupLeaderboardPages.helper';
import { sliceLeaderboardForTvDisplay } from '../helpers/squadTvLeaderboard.helper';
import type { GroupLeaderboardPeriod } from './useWorldCupGroupLeaderboardQuery';
import type { LeaderboardEntry } from '../types/worldcup.types';

interface UseSquadTvLeaderboardQueryOptions {
  enabled?: boolean;
  period?: GroupLeaderboardPeriod;
}

export function useSquadTvLeaderboardQuery(
  competitionId: string,
  groupId: string,
  options?: UseSquadTvLeaderboardQueryOptions,
) {
  const apiClient = useApiClient();
  const period = options?.period ?? 'overall';
  const enabled = (options?.enabled ?? true) && groupId.length > 0;

  const query = useQuery({
    queryKey: worldCupQueryKeys.groupLeaderboardTv(competitionId, groupId, period),
    queryFn: () =>
      fetchAllGroupLeaderboardPages({
        apiClient,
        competitionId,
        groupId,
        period,
      }),
    enabled,
    staleTime: STALE_TIME_WORLD_CUP_LEADERBOARD_MS,
    gcTime: GC_TIME_WORLD_CUP_MS,
    refetchOnWindowFocus: false,
  });

  const { top3, rest } = useMemo(() => {
    const entries = query.data?.entries ?? [];
    return sliceLeaderboardForTvDisplay(entries);
  }, [query.data?.entries]);

  const entries: LeaderboardEntry[] = query.data?.entries ?? [];

  return {
    entries,
    top3,
    rest,
    isLoading: query.isLoading,
    isError: query.isError,
    isFetching: query.isFetching,
  };
}
